import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { secureStorage } from '@/utils/secureStorage'
import {
  applySessionTokens,
  clearAdminSessionStorage,
  ensureValidAccessToken,
} from '@/utils/authSession'
import { authApi, clearDashboardApiCache } from '@/services/api'
import { useProjectContextStore } from '@/stores/projectContext'
import type { AdminRegisterRequest } from '@/types/auth'
import { evaluateLicenseStatus } from '@/composables/useLicenseStatus'

function shouldLoadAdminProjects(admin: { role?: string; license?: unknown } | null | undefined): boolean {
  if (!admin || admin.role === 'superadmin') {
    return true
  }
  return evaluateLicenseStatus(admin.license as import('@/types/admin').License | undefined).isValid
}

export const useAuthStore = defineStore('auth', () => {
  const token = ref<string | null>(secureStorage.getToken())
  const user = ref<any | null>(null)
  const router = useRouter()
  const projectContext = useProjectContextStore()

  let profileFetchPromise: Promise<void> | null = null

  const fetchUserProfile = async (options?: { force?: boolean }) => {
    if (!options?.force && profileFetchPromise) {
      return profileFetchPromise
    }

    profileFetchPromise = (async () => {
      try {
        const adminData = await authApi.getAdminProfile()
        user.value = adminData
        if (adminData?.role === 'superadmin') {
          await projectContext.fetchSuperadminProjectList()
        } else if (shouldLoadAdminProjects(adminData)) {
          await projectContext.fetchMyAssignedProjects()
        } else {
          projectContext.clearProjectContext()
        }
        if (
          adminData?.role === 'admin' &&
          projectContext.projects.length === 0 &&
          router.currentRoute.value.path !== '/dashboard'
        ) {
          await router.replace('/dashboard')
        }
      } catch {
        handleLogout()
      } finally {
        profileFetchPromise = null
      }
    })()

    return profileFetchPromise
  }

  const login = async (email: string, password: string) => {
    try {
      return adminLogin(email, password)
    } catch (error: any) {
      throw error
    }
  }

  const adminLogin = async (email: string, password: string) => {
    try {
      const response = await authApi.adminLogin(email, password)

      token.value = response.token
      user.value = response.admin
      applySessionTokens(response.token, response.refresh_token)
      secureStorage.setIsAdmin(true)

      if (response?.admin?.role === 'superadmin') {
        await projectContext.fetchSuperadminProjectList()
      } else if (shouldLoadAdminProjects(response.admin)) {
        await projectContext.fetchMyAssignedProjects()
      } else {
        projectContext.clearProjectContext()
      }
      if (response?.admin?.role === 'superadmin') {
        if (projectContext.projects.length === 0) {
          await router.push({ path: '/admins', query: { tab: 'projects' } })
        } else {
          await router.push('/dashboard')
        }
      } else {
        await router.push('/dashboard')
      }
    } catch (error: any) {
      throw error
    }
  }

  const registerAdmin = async (adminData: AdminRegisterRequest) => {
    try {
      const response = await authApi.registerAdmin(adminData)
      return response
    } catch (error: any) {

      throw error
    }
  }

  const handleLogout = () => {
    token.value = null
    user.value = null
    clearAdminSessionStorage()
    projectContext.clearProjectContext()
    clearDashboardApiCache()
  }

  const applyTokensFromRefresh = (
    accessToken: string,
    refreshToken?: string,
    admin?: Record<string, unknown> | null,
  ) => {
    token.value = accessToken
    applySessionTokens(accessToken, refreshToken)
    if (admin) {
      void syncAdminSession(admin)
    }
  }

  const syncAdminSession = async (admin: Record<string, unknown>) => {
    const hadValidLicense = shouldLoadAdminProjects(user.value)
    user.value = admin
    if (admin?.role === 'superadmin') {
      await projectContext.fetchSuperadminProjectList()
      return
    }
    const hasValidLicense = shouldLoadAdminProjects(admin)
    if (hasValidLicense) {
      if (!hadValidLicense || projectContext.projects.length === 0) {
        await projectContext.fetchMyAssignedProjects()
      }
      return
    }
    projectContext.clearProjectContext()
  }

  const ensureSession = async (): Promise<boolean> => {
    const refreshed = await ensureValidAccessToken()
    if (!refreshed) {
      return false
    }
    token.value = refreshed.token
    if (refreshed.admin) {
      await syncAdminSession(refreshed.admin)
    }
    return true
  }

  const logout = async () => {
    try {
      const refreshToken = secureStorage.getRefreshToken()
      if (refreshToken) {
        await authApi.adminLogout(refreshToken)
      }
    } catch {
    }

    token.value = null
    clearAdminSessionStorage()

    await router.push('/login')

    user.value = null
    projectContext.clearProjectContext()
    clearDashboardApiCache()
  }

  const isAuthenticated = () => {
    const authenticated = !!token.value && !secureStorage.isTokenExpired()
    return authenticated
  }

  const initialize = () => {
    const isAdmin = secureStorage.getIsAdmin()

    if (!isAdmin) {
      handleLogout()
      return
    }

    void ensureSession().then((ok) => {
      if (!ok) {
        handleLogout()
        return
      }
      fetchUserProfile()
    })

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState !== 'visible' || !secureStorage.getIsAdmin()) {
          return
        }
        void ensureSession().then((ok) => {
          if (!ok) {
            return
          }
          if (user.value?.role === 'admin' && !shouldLoadAdminProjects(user.value)) {
            void fetchUserProfile({ force: true })
          }
        })
      })
    }
  }

  initialize()

  return {
    token,
    user,
    login,
    logout,
    registerAdmin,
    isAuthenticated,
    ensureSession,
    applyTokensFromRefresh,
    syncAdminSession,
    fetchUserProfile,
    adminLogin
  }
})